import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { formatTime, getCheckoutTime, isManualJob } from '../../../utils/job-utils';
import { formatUserName } from '../../../utils/user-utils';
import { AreaIndicator } from '../../../components/job-indicators';
import NextCheckInDisplay from '../../../components/NextCheckInDisplay';
import { getServiceTypeBorderColor } from '../../../utils/service-type-utils';
import DueByDisplay from '../../../components/DueByDisplay';

// Helper to format area name: remove underscores and capitalize each word
const formatAreaName = (area) => {
  if (!area) return '';
  return area
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Define the component function first
function JobCard({ 
  job, 
  index, 
  onClick, 
  renderBadges,
  renderActions,
  leftBorderColor, 
  bgColor = 'bg-gray-50',
  selected,
  onSelect,
  onJobViewDetails,
  isSchedulePage = false,
  isSelectionMode = false,
  isSelected = false
}) {

  // Get color class for the left strip based on job category
  const getCategoryColorClass = (category) => {
    switch (category) {
      case 'CLEANING':
        return 'bg-blue-500';
      case 'WALKTHROUGH':
        return 'bg-purple-500';
      case 'PREARRIVAL_INSPECTION':
      case 'INSPECTION':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Check if this is a flip job first
  const isFlipJob = job.isFlip;

  // Check if we can use the service type color for border
  const shouldUseServiceTypeColor = !leftBorderColor && job.serviceType?.color;
  
  // Get border class, with priority for passed leftBorderColor, then service type color, then category color
  const borderClass = leftBorderColor 
    ? `border-l-[24px] ${leftBorderColor}` 
    : shouldUseServiceTypeColor
      ? `border-l-[24px] relative`
      : '';

  // Service type color style for direct CSS if needed
  const serviceTypeColorStyle = shouldUseServiceTypeColor 
    ? { 
        '--service-type-color': isFlipJob ? '#ef4444' : job.serviceType.color, // Red for flip jobs
        borderLeftColor: isFlipJob ? '#ef4444' : job.serviceType.color // Red for flip jobs
      } 
    : {};

  // Check if the job is in progress
  const isInProgress = job.status === 'IN_PROGRESS';

  // Check if this is a flip job and override styling
  // Apply light green background for completed and ready for inspection jobs on Schedule page
  const shouldApplyLightGreen = isSchedulePage && (job.status === 'COMPLETED' || job.status === 'READY_FOR_INSPECTION');
  // Apply selection styling if in selection mode and selected
  const selectionBgColor = isSelectionMode && isSelected ? 'bg-blue-50' : null;
  const finalBgColor = selectionBgColor || (shouldApplyLightGreen ? 'bg-green-50' : bgColor);

  // Handle checkbox click for selection mode
  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(job.id, e.target.checked);
    }
  };

  // Handle card click in selection mode
  const handleCardClick = () => {
    if (isSelectionMode && onSelect) {
      onSelect(job.id, !isSelected);
    }
  };
  const finalBorderClass = shouldUseServiceTypeColor
    ? isFlipJob 
      ? 'border-l-[24px] relative border-2 border-red-500'  // Red left border + red outer border (2px) for flip
      : 'border-l-[24px] relative border border-gray-200'   // Service type color + gray border
    : isFlipJob
      ? 'border-l-[24px] border-2 border-red-500'           // Gray left border + red outer border (2px) for flip
      : borderClass || 'border border-gray-200';            // Default border

  return (
    <div className="relative mb-4">
      {/* Main Card */}
      <div 
        className={`${finalBgColor} shadow ${finalBorderClass} rounded-lg relative ${isSelectionMode ? 'cursor-pointer' : ''} ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
        style={serviceTypeColorStyle}
        onClick={isSelectionMode ? handleCardClick : undefined}
        role={isSelectionMode ? "button" : undefined}
        tabIndex={isSelectionMode ? 0 : undefined}
        onKeyDown={isSelectionMode ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleCardClick(); } : undefined}
        aria-pressed={isSelectionMode ? isSelected : undefined}
      >
        {/* Selection Mode Checkbox */}
        {isSelectionMode && (
          <div className="absolute top-3 right-3 z-10">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              aria-label={`Select job at ${job.property?.name || 'property'}`}
            />
          </div>
        )}
        
        <div className="flex">
          {!leftBorderColor && !shouldUseServiceTypeColor && (
            <div className={`w-24 ${getCategoryColorClass(job.category)} rounded-l-lg`}></div>
          )}
          <div className="flex-1 p-2 lg:p-4">
            {/* In Progress status indicator - above property name */}
            {isInProgress && (
              <div className="flex items-center mb-2">
                <div className="relative mr-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                </div>
                <div className="text-xs text-emerald-600 font-medium">
                  In progress
                </div>
              </div>
            )}

            {/* Associated Cleaning Job Status indicator - for inspection jobs */}
            {job.category === 'INSPECTION' && job.associatedCleaningJobStatus === 'IN_PROGRESS' && (
              <div className="flex items-center mb-2">
                <div className="relative mr-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
                </div>
                <div className="text-xs text-blue-600 font-medium">
                  Cleaning in progress
                </div>
              </div>
            )}

            {/* Clean Completed indicator - for inspection jobs with completed cleaning */}
            {job.category === 'INSPECTION' && (job.associatedCleaningJobStatus === 'READY_FOR_INSPECTION' || job.associatedCleaningJobStatus === 'COMPLETED') && (
              <div className="flex items-center mb-2">
                <div className="relative mr-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-2 h-2 bg-indigo-500 rounded-full"></div>
                </div>
                <div className="text-xs text-indigo-600 font-medium">
                  Clean completed
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-5 gap-2">
              {/* Left Column - Takes 2/3 space */}
              <div className="col-span-3 space-y-2">
                {/* Property name with recurring indicator */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onJobViewDetails && onJobViewDetails(job.id)}
                    className="font-medium text-gray-900 hover:text-blue-600 transition-colors duration-200 text-left"
                  >
                    {job.property.name}
                  </button>
                  {job.isRecurringJob && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">
                      <svg 
                        className="w-3 h-3" 
                        viewBox="0 0 16 16" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.5 8.5C13.5 11.537 11.037 14 8 14C4.963 14 2.5 11.537 2.5 8.5C2.5 5.463 4.963 3 8 3C9.5 3 10.8 3.7 11.7 4.8"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          fill="none"
                        />
                        <path
                          d="M10.5 2.5L11.7 4.8L9.4 5.2"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                        <path
                          d="M2.5 7.5C2.5 4.463 4.963 2 8 2C9.5 2 10.8 2.7 11.7 3.8"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          fill="none"
                          opacity="0.6"
                        />
                        <path
                          d="M5.5 13.5L4.3 11.2L6.6 10.8"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                          opacity="0.6"
                        />
                      </svg>
                      Recurring
                    </div>
                  )}
                </div>

                {/* Start Time */}
                <div className="text-sm text-gray-700">
                  {job.hasTimeOverride ? (
                    <div>
                      <span className="flex items-center">
                        <span>Start Time:</span>
                        {getCheckoutTime(job) && (
                          <span className="ml-1 line-through text-gray-400">{formatTime(getCheckoutTime(job))}</span>
                        )}
                      </span>
                      <span className="flex items-center text-sky-600 font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        {formatTime(job.startTime)}
                      </span>
                    </div>
                  ) : (
                    <span>Start Time: {formatTime(getCheckoutTime(job))}</span>
                  )}
                </div>

                {/* Date and Due by */}
                <div className="flex items-center text-xs flex-wrap gap-2">
                  <span className="text-gray-400">
                    {format(new Date(job.scheduledDate), 'MMM d, yyyy')}
                  </span>
                  {/* Only show "Due by" for jobs that have reservations */}
                  {job.property && (job.reservation || job.reservationId) && (
                    <span className="text-blue-600 font-medium">
                    Due by: <DueByDisplay job={job} />
                    </span>
                  )}
                </div>

                {/* Assigned To */}
                {job.assignedTeamId ? (
                  // Team assignment - show team info
                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-xs lg:text-sm">
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {job.status === 'COMPLETED' 
                      ? `Completed by Team: ${job.assignedTeam?.name || `Team ${job.assignedTeamId}`}`
                      : `Assigned to Team: ${job.assignedTeam?.name || `Team ${job.assignedTeamId}`}`
                    }
                  </div>
                ) : job.assignedTo ? (
                  // Individual assignment - show user info
                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-green-100 text-green-800 text-xs lg:text-sm">
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {job.status === 'COMPLETED' 
                      ? `Completed by: ${formatUserName(job.assignedTo)}`
                      : `Assigned to: ${formatUserName(job.assignedTo)}`
                    }
                  </div>
                ) : (
                  // Unassigned
                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-orange-100 text-orange-800 text-sm">
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Unassigned
                  </div>
                )}
              </div>

              {/* Right Column - Badges only */}
              <div className="flex flex-col justify-start col-span-2">
                {/* Indicators section */}
                <div className="flex flex-wrap flex-row-reverse justify-start gap-1 whitespace-nowrap">
                  {renderBadges && renderBadges(job)}
                </div>
              </div>
            </div>

            {/* Bottom section with View Details and Area/Counter */}
            <div className="mt-3 grid grid-cols-2 gap-4">
              {/* Left - Actions section */}
              <div className="flex items-center justify-start">
                {renderActions && renderActions(job)}
              </div>

              {/* Right - Area and counter section */}
              <div className="flex justify-end items-center gap-2">
                {/* Area indicator */}
                {job.property.area && (
                  <AreaIndicator area={job.property.area} />
                )}

                {/* Job number */}
                <div className="flex items-center justify-center h-5 w-5 text-xs text-gray-500 font-mono bg-gray-100 rounded-full">
                  {index + 1}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Status Tab - REMOVED as requested */}
    </div>
  );
}

// Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Compare job ID and key properties
  if (prevProps.job?.id !== nextProps.job?.id) return false;
  if (prevProps.job?.status !== nextProps.job?.status) return false;
  if (prevProps.job?.assignedTo?.id !== nextProps.job?.assignedTo?.id) return false;
  if (prevProps.job?.assignedTeamId !== nextProps.job?.assignedTeamId) return false;
  if (prevProps.job?.assignedTeam?.id !== nextProps.job?.assignedTeam?.id) return false;
  if (prevProps.selected !== nextProps.selected) return false;
  if (prevProps.index !== nextProps.index) return false;
  if (prevProps.leftBorderColor !== nextProps.leftBorderColor) return false;
  if (prevProps.bgColor !== nextProps.bgColor) return false;
  
  // Selection mode props
  if (prevProps.isSelectionMode !== nextProps.isSelectionMode) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  
  // 🎯 CRITICAL: Check computed flag changes to ensure immediate updates after reservation changes
  if (prevProps.job?.isEarlyCheckin !== nextProps.job?.isEarlyCheckin) return false;
  if (prevProps.job?.isFlip !== nextProps.job?.isFlip) return false;
  if (prevProps.job?.hasDateOverride !== nextProps.job?.hasDateOverride) return false;
  if (prevProps.job?.hasTimeOverride !== nextProps.job?.hasTimeOverride) return false;
  
  // Check reservation-related fields that affect late checkout calculations and "Due by" time
  if (prevProps.job?.reservation?.override_check_out !== nextProps.job?.reservation?.override_check_out) return false;
  if (prevProps.job?.reservation?.override_check_in !== nextProps.job?.reservation?.override_check_in) return false;
  if (prevProps.job?.reservation?.has_time_override !== nextProps.job?.reservation?.has_time_override) return false;
  if (prevProps.job?.previousReservation?.override_check_out !== nextProps.job?.previousReservation?.override_check_out) return false;
  
  // If all key properties are the same, don't re-render
  return true;
};

// Export the component with memoization for better performance
// This prevents unnecessary re-renders when parent components update
export default React.memo(JobCard, arePropsEqual); 